// AnimatedVFX.tsx
import React, { useEffect, useState, useRef, useMemo, memo } from "react";
import { VFX_SPRITE_FRAME_COUNT_DATA } from "@/data/vfx-sprite-frame-count-data";
import { VFX_SPRITE_FRAME_ANCHOR_DATA } from "@/data/vfx-sprite-frame-anchor-data";
import { Position } from "types/battle-engine/battle";

// Cache for loaded images (shared across all instances)
const imageCache = new Map<string, HTMLImageElement>();

// Global animation frame scheduler for all VFX
let rafId: number | null = null;
const animatedVFXSprites = new Set<() => void>();

function scheduleAnimationFrame() {
  if (rafId !== null) return;
  rafId = requestAnimationFrame(() => {
    rafId = null;
    animatedVFXSprites.forEach((update) => update());
    if (animatedVFXSprites.size > 0) {
      scheduleAnimationFrame();
    }
  });
}

export type AnimatedVFXProps = {
  id: string;
  spriteUrl: string;
  spriteScale?: number;
  spriteAlpha?: number;
  position: Position;
  loops?: number;
  speed?: number;
  ping_pong?: boolean;
  anchorTarget?: () => Position | null; // Callback to get target position
  y_offset?: number;
  falling?: boolean;
  duration_ticks?: number;
  onComplete?: () => void;
  className?: string;
  style?: React.CSSProperties;
  zIndex?: number;
};

export const AnimatedVFX = memo(
  ({
    id,
    spriteUrl,
    spriteScale = 1,
    spriteAlpha = 1,
    position,
    loops = 1,
    speed = 1,
    ping_pong = false,
    anchorTarget,
    y_offset = 0,
    falling = false,
    duration_ticks = 0,
    onComplete,
    className,
    style,
    zIndex = 9998,
  }: AnimatedVFXProps) => {
    const [spriteData, setSpriteData] = useState<{
      src: string;
      frameWidth: number;
      frameHeight: number;
      frames: number;
    } | null>(null);
    const [frameKey, setFrameKey] = useState(0); // Force re-render on frame change
    const [positionKey, setPositionKey] = useState(0); // Force re-render on position change
    const [isFinished, setIsFinished] = useState(false);

    const currentFrameRef = useRef<number>(0);
    const frameTimeRef = useRef<number>(0);
    const lastUpdateRef = useRef<number>(0);
    const isAnimatingRef = useRef<boolean>(false);
    const loopCountRef = useRef<number>(1);
    const animationSpeedRef = useRef<number>(speed);
    const fallingTickRef = useRef<number>(0);

    useEffect(() => {
      const initAnimatedVFX = async () => {
        const frameCount = VFX_SPRITE_FRAME_COUNT_DATA[spriteUrl];
        if (!frameCount) {
          console.error(`Frame count not found for VFX sprite: ${spriteUrl}`);
          return;
        }

        // Load sprite image
        let spriteImage = imageCache.get(spriteUrl);
        if (!spriteImage) {
          spriteImage = new Image();
          spriteImage.src = spriteUrl;
          imageCache.set(spriteUrl, spriteImage);
        }

        const setSpriteWhenReady = () => {
          currentFrameRef.current = 0;
          frameTimeRef.current = 0;
          loopCountRef.current = 1;
          setIsFinished(false);
          lastUpdateRef.current = performance.now();

          const frameWidth = spriteImage!.width / frameCount;
          const frameHeight = spriteImage!.height;

          setSpriteData({
            src: spriteImage!.src,
            frameWidth,
            frameHeight,
            frames: frameCount,
          });
        };

        if (spriteImage.complete) {
          setSpriteWhenReady();
        } else {
          spriteImage.onload = setSpriteWhenReady;
        }
      };

      initAnimatedVFX();
    }, [spriteUrl]);

    // Animation update function (called by global RAF)
    const updateAnimation = useRef<(() => void) | undefined>(undefined);

    useEffect(() => {
      if (!spriteData) {
        if (updateAnimation.current) {
          animatedVFXSprites.delete(updateAnimation.current);
          updateAnimation.current = undefined;
          isAnimatingRef.current = false;
        }
        return;
      }

      const { frames } = spriteData;
      const defaultFrameDuration = 33.33; // ~30fps

      updateAnimation.current = () => {
        const now = performance.now();
        const delta = now - lastUpdateRef.current;
        const currentFrame = currentFrameRef.current;
        const frameDurationMs =
          defaultFrameDuration / animationSpeedRef.current;

        frameTimeRef.current += delta;
        lastUpdateRef.current = now;

        // Handle falling animation tick
        if (falling && duration_ticks && y_offset !== 0) {
          fallingTickRef.current++;
        }

        if (frameTimeRef.current >= frameDurationMs) {
          frameTimeRef.current = 0;
          let nextFrame = currentFrame + 1;

          // Handle ping pong animation
          if (ping_pong && loopCountRef.current > 1) {
            if (animationSpeedRef.current > 0 && nextFrame >= frames) {
              nextFrame = frames - 2;
              animationSpeedRef.current = -animationSpeedRef.current;
            } else if (animationSpeedRef.current < 0 && nextFrame < 0) {
              nextFrame = 1;
              animationSpeedRef.current = -animationSpeedRef.current;
            }
          } else {
            nextFrame = nextFrame % frames;
          }

          currentFrameRef.current = nextFrame;

          // Check if animation completed
          if (nextFrame === 0) {
            if (loopCountRef.current >= loops) {
              // Animation completed
              onComplete?.();
              setIsFinished(true);
              // Pause the animation and hide
              if (updateAnimation.current) {
                animatedVFXSprites.delete(updateAnimation.current);
                updateAnimation.current = undefined;
              }
              isAnimatingRef.current = false;
              setFrameKey(0);
              return;
            }
            loopCountRef.current++;
          }

          // Force re-render by updating frame key
          setFrameKey((prev) => prev + 1);
        }

        // Update position if anchored
        if (anchorTarget) {
          setPositionKey((prev) => prev + 1);
        }
      };

      animatedVFXSprites.add(updateAnimation.current);
      isAnimatingRef.current = true;
      lastUpdateRef.current = performance.now();
      scheduleAnimationFrame();

      return () => {
        if (updateAnimation.current) {
          animatedVFXSprites.delete(updateAnimation.current);
          updateAnimation.current = undefined;
          isAnimatingRef.current = false;
        }
      };
    }, [spriteData, loops, speed, ping_pong, onComplete]);

    const backgroundPosition = useMemo(() => {
      if (!spriteData) return "0px 0px";
      return `-${currentFrameRef.current * spriteData.frameWidth}px 0px`;
    }, [spriteData, frameKey]);

    const currentPosition = useMemo(() => {
      if (!anchorTarget) return position;

      const targetPosition = anchorTarget();
      if (!targetPosition) return position;

      let finalPosition = { ...targetPosition };

      if (falling && duration_ticks && y_offset !== 0) {
        const progress = Math.min(fallingTickRef.current / duration_ticks, 1);
        const currentYOffset = y_offset * (1 - progress);
        finalPosition.y += currentYOffset;

        // Auto-complete when falling animation finishes
        if (progress >= 1 && !isAnimatingRef.current) {
          onComplete?.();
        }
      } else {
        finalPosition.y += y_offset;
      }

      return finalPosition;
    }, [
      position,
      anchorTarget,
      y_offset,
      falling,
      duration_ticks,
      positionKey,
    ]);

    const anchor = VFX_SPRITE_FRAME_ANCHOR_DATA[spriteUrl] ?? {
      x: 0.5,
      y: 0.5,
    };

    if (!spriteData || isFinished) {
      return null; // Or loading placeholder
    }

    return (
      <div
        className={`absolute ${className ?? ""}`}
        id={id}
        style={{
          left:
            currentPosition.x - spriteData.frameWidth * anchor.x * spriteScale,
          top:
            currentPosition.y - spriteData.frameHeight * anchor.y * spriteScale,
          width: spriteData.frameWidth,
          height: spriteData.frameHeight,
          backgroundImage: `url(${spriteData.src})`,
          backgroundSize: `${spriteData.frameWidth * spriteData.frames}px ${
            spriteData.frameHeight
          }px`,
          backgroundPosition,
          imageRendering: "pixelated",
          transform: `scale(${spriteScale})`,
          transformOrigin: `${anchor.x * 100}% ${anchor.y * 100}%`,
          opacity: spriteAlpha,
          zIndex,
          pointerEvents: "none",
          ...style,
        }}
        aria-hidden
      />
    );
  },
);

AnimatedVFX.displayName = "AnimatedVFX";
